// A site's firmware upgrade run, on the site's access point list (ADR-0463, Accepted 2026-10-23;
// built by #1967): the Upgrade site panel, which shows the list a run would approve and every access
// point left out of it, and the run's own view while and after it runs.
//
// **Everything here draws the server's answer and decides nothing.** The candidates, the reasons an
// access point is left out, how many go at once and every consequence sentence are the preview's
// (`GET /sites/{id}/upgrade-runs:preview`); each device's state and the stop are the run's. The one
// thing the operator changes is the order, and the click sends the list exactly as shown.
import { useMemo, useState } from "react";
import { api, idempotencyKey } from "../../api/client";
import { SKIP, useQuery } from "../../api/hooks";
import { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { EntityRef } from "../../shell/EntityRef";
import type { Entity } from "../../shell/entity";
import { Loading, Pill, ProblemBox } from "../../ui";
import { useDeliveryChanges } from "./delivery";
import { wordFor } from "../../api/openEnum";

type Preview = components["schemas"]["SiteUpgradePreview"];
type Runs = components["schemas"]["SiteUpgradeRuns"];
type Run = components["schemas"]["SiteUpgradeRun"];

/** A device's state in a run, in the words the delivery card uses for the same states. */
// Restates ADR-0463 decision 7 (`7a`) — each device reads its delivery's state in ADR-0371 `b1`'s
// words, plus *skipped* with the reason and *not reached*.
const STATE_LABEL: Record<components["schemas"]["SiteUpgradeRunDeviceState"], string> = {
  waiting: "Waiting", not_reached: "Not reached", skipped: "Skipped",
  handing_over: "Starting", accepted: "Starting", in_progress: "In progress",
  delivered: "Delivered", refused: "Not sent", failed: "Failed", unknown: "Unknown",
};

const RUN_LABEL: Record<components["schemas"]["SiteUpgradeRunState"], string> = { running: "Running", stopped: "Stopped", completed: "Completed" };

/** An access point's serial and, where it has one, its name, each with the one
 * menu (#2367), drawn as they were: the serial first. Its site is the scope's. */
function AccessPoint({ serial, name, siteId }: { serial: string; name?: string; siteId: string }) {
  const device: Entity = { kind: "device", serial, name: name || undefined, switch: false, siteId };
  const id = <EntityRef entity={device} label={`Menu for ${serial}`}><span className="num">{serial}</span></EntityRef>;
  return name ? <>{id} <EntityRef entity={device}><span>{name}</span></EntityRef></> : id;
}

/** The run's own view: each device in the order approved, and why it stopped. */
function RunView({ run, siteId, mayStop, onStop, busy }: { run: Run; siteId: string; mayStop: boolean; onStop(): void; busy: boolean }) {
  return (
    <div className="site-upgrade-run">
      <p>
        <Pill tone={run.state === "stopped" ? "warn" : run.state === "completed" ? "good" : "info"}>{wordFor(RUN_LABEL, run.state)}</Pill>{" "}
        {run.stop_text && <span>{run.stop_text}</span>}
      </p>
      <ol className="site-upgrade-list">
        {run.devices.map((d) => (
          <li key={d.serial}>
            <AccessPoint serial={d.serial} siteId={siteId} />{d.canary && <> <Pill tone="info">Canary</Pill></>}{" "}
            <Pill>{wordFor(STATE_LABEL, d.state)}</Pill>
            {d.came_back === true && <> <Pill tone="good">Came back</Pill></>}
            {d.came_back === false && <> <Pill tone="critical">Did not come back</Pill></>}
            {d.skip_text && <span className="muted"> {d.skip_text}</span>}
          </li>
        ))}
      </ol>
      {run.state === "running" && mayStop && (
        <div className="actions">
          {/* Restates ADR-0463 decision 5 (`5a`) — a stop starts nothing new; an upgrade already under
              way finishes, and nothing is rolled back. */}
          <button type="button" className="btn" disabled={busy} onClick={onStop}>Stop the run</button>
          <span className="muted"> Upgrades already under way finish.</span>
        </div>
      )}
    </div>
  );
}

/** The panel before the click: the server's consequences, the list in the operator's order with
 * the canary first, and every access point left out with its reason. */
function UpgradePanel({ siteId, following, onDone, onClose }: { siteId: string; following?: string; onDone(): void; onClose(): void }) {
  const preview = useQuery<Preview>(() => api.GET("/sites/{id}/upgrade-runs:preview", { params: { path: { id: siteId }, query: following ? { following } : {} } }), [siteId, following]);
  const m = useMutation();
  const shown = preview.status === "ready" ? preview.data : undefined;
  // The operator's order, kept by serial; the server's stable order until it is changed.
  const [order, setOrder] = useState<string[]>();
  const list = useMemo(() => {
    const byserial = new Map((shown?.candidates ?? []).map((c) => [c.serial, c] as const));
    const serials = order?.filter((s) => byserial.has(s)) ?? [...byserial.keys()];
    return serials.map((s) => byserial.get(s)!);
  }, [shown, order]);
  const move = (i: number, by: number) => {
    const next = list.map((c) => c.serial);
    const j = i + by;
    if (j < 0 || j >= next.length) return;
    const a = next[i]!, b = next[j]!;
    next[i] = b;
    next[j] = a;
    setOrder(next);
  };
  const go = async () => {
    const r = await m.runWith(() => api.POST("/sites/{id}/upgrade-runs", {
      params: { path: { id: siteId }, header: { "Idempotency-Key": idempotencyKey() } },
      body: { devices: list.map((c) => ({ serial: c.serial, artifact: c.artifact })) },
    }));
    if (r.ok) onDone();
    else preview.reload();
  };
  return (
    <section className="card site-upgrade-panel" aria-label="Upgrade site">
      <div className="card-head">
        <h2>Upgrade site</h2>
        <button type="button" className="btn btn-quiet" aria-label="Close" onClick={onClose}>×</button>
      </div>
      {preview.status === "loading" && <Loading label="Reading which access points can be upgraded" />}
      {preview.status === "error" && <ProblemBox problem={preview.problem} fold />}
      {shown && (
        <>
          {/* Restates ADR-0463 decisions 2.1, 6.1, 5, 8.3 and 6.3 (`2.1a`, `6.1a`, `5a`, `8.3a`, `6.3a`) — the
              server's sentences, said before the click: the list is frozen, the canary goes alone, how many
              at once, the run stops at the first that does not come back, such a unit may need a person,
              and a wired dependency cannot be read. */}
          <ul className="site-upgrade-consequences">
            {shown.consequences.map((c) => <li key={c}>{c}</li>)}
          </ul>
          {shown.catalog.state === "unavailable" && <p className="warn">The firmware catalog could not be read just now.</p>}
          {list.length === 0 ? <p className="muted">No access point at this site can be upgraded by a site run now.</p> : (
            <ol className="site-upgrade-list">
              {list.map((c, i) => (
                <li key={c.serial}>
                  <AccessPoint serial={c.serial} name={c.name} siteId={siteId} />{i === 0 && <> <Pill tone="info">Canary</Pill></>}{" "}
                  <span className="muted">{c.running_firmware} → {c.predicted_firmware}</span>{" "}
                  <button type="button" className="btn btn-quiet btn-sm" aria-label={`Move ${c.serial} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button type="button" className="btn btn-quiet btn-sm" aria-label={`Move ${c.serial} down`} disabled={i === list.length - 1} onClick={() => move(i, 1)}>↓</button>
                </li>
              ))}
            </ol>
          )}
          {shown.left_out.length > 0 && (
            <>
              <h3>Left out</h3>
              <ul className="site-upgrade-left-out">
                {shown.left_out.map((l) => <li key={l.serial}><AccessPoint serial={l.serial} name={l.name} siteId={siteId} />: {l.reason_text}</li>)}
              </ul>
            </>
          )}
          {m.problem && <ProblemBox problem={m.problem} fold />}
          <div className="actions">
            {/* Restates ADR-0463 decision 2.1 (`2.1a`) — one click approves every access point listed, each
                with its firmware, in this order. */}
            <button type="button" className="btn btn-primary" disabled={m.busy || list.length === 0} onClick={() => void go()}>
              {list.length === 1 ? "Upgrade 1 access point" : `Upgrade ${list.length} access points`}
            </button>
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
          </div>
        </>
      )}
    </section>
  );
}

/** The site's run: its newest run's view, and the way to start one. */
export default function SiteUpgrade({ siteId, mayCommand }: { siteId: string; mayCommand: boolean }) {
  const runs = useQuery<Runs>(() => siteId ? api.GET("/sites/{id}/upgrade-runs", { params: { path: { id: siteId } } }) : SKIP, [siteId]);
  useDeliveryChanges(runs.reload);
  const m = useMutation();
  const [panel, setPanel] = useState<{ following?: string }>();
  const newest = runs.status === "ready" ? runs.data.runs[0] : undefined;
  const running = newest?.state === "running";
  const notReached = newest?.state === "stopped" ? newest.devices.filter((d) => d.state === "not_reached").length : 0;
  const stop = async () => {
    if (!newest) return;
    const r = await m.runWith(() => api.POST("/sites/{id}/upgrade-runs/{run_id}:stop", {
      params: { path: { id: siteId, run_id: newest.id }, header: { "Idempotency-Key": idempotencyKey() } },
    }));
    if (r.ok) runs.reload();
  };
  if (!mayCommand && !newest) return null;
  return (
    <div className="site-upgrade">
      {newest && <RunView run={newest} siteId={siteId} mayStop={mayCommand} onStop={() => void stop()} busy={m.busy} />}
      {m.problem && <ProblemBox problem={m.problem} fold />}
      {mayCommand && !running && !panel && (
        <div className="actions">
          <button type="button" className="btn" onClick={() => setPanel({})}>Upgrade site…</button>
          {/* Restates ADR-0463 decision 5.1 (`5.1a`) — a stopped run is never resumed; a new run lists the
              devices it did not reach and is approved afresh. */}
          {notReached > 0 && newest && (
            <button type="button" className="btn" onClick={() => setPanel({ following: newest.id })}>
              {notReached === 1 ? "New run for the 1 not reached…" : `New run for the ${notReached} not reached…`}
            </button>
          )}
        </div>
      )}
      {panel && <UpgradePanel siteId={siteId} following={panel.following} onClose={() => setPanel(undefined)} onDone={() => { setPanel(undefined); runs.reload(); }} />}
    </div>
  );
}
